import utils from "@/utils";
import { Controller } from "@hotwired/stimulus";
export interface FrameUpdate {
    content: string;
    variables: unknown;
}

export interface Form {
    getVariables(): unknown;
    setVariables(variables: unknown): void;
}

export interface FrameApi {
    getContent(element: HTMLElement, variables: unknown): Promise<string>;
}

/**
 * Some questions to consider:
 * 1. Can the full state be stored in the URL, or does it need to be stored in the `history.state` or `sessionStorage`? Storing the full state in the URL can lead to long and complex URLs, but greatly simplifies the implementation.
 * 2. How will the state be restored on page refresh or when navigating via the back/forward button? The server can easily access URL parameters, but does not have direct access to state stored in the browser. Once the DOM is loaded, the browser will have to send the state in a request up to the server, then update the DOM accordingly. This approach causes screen flicker, but it can potentially be mitigated through use of a placeholder inside the frame where the content will be displayed. Another approach would be to use the session storage on the server to mirror the state of the browser, but this breaks down (or becomes hard to keep track of) in cases where the user has multiple tabs
 * 3. Will the frame be preloaded with the initial state, or will it be empty until the form is submitted? For example, a report feature may be preloaded, with options to customize thereafter. A search feature would typically not be preloaded, since the user has to enter a search term before anything can be displayed. When preloading, it's best to keep all of the variables in the URL, so that the server preload can account for any changes made prior to refreshing the page or navigating via the back/forward button.
 * 4. If the user interacts with the form multiple times, making changes and resubmitting, should each interaction be saved in the history (with `pushState`), or should only the final state be saved (with `replaceState`)? For example, if the user searches for "Adam" and then searches for "Bridgette", it might make sense for the back button to take the user back to the search for "Adam". Google search works this way. However, if the user is making a configuration change to a report, and tweaking it until it is just right, it might feel odd and cumbersome for the back button to walk the user back through each of those tweaks. If undo functionality is needed, it probably should not be implemented with the back button, but with a separate undo button.
 */
export interface FrameHistoryStrategy {
    captureState(variables: unknown): void;
}

export interface FrameContext {
    form: Form;
    api: FrameApi;
    history: FrameHistoryStrategy;
}

/** 
 * Uses a form submission to asynchronously update the content of a frame.
 * The async request will include the form variables, and will be sent to the url specified in the form action.
 * Also updates the browser history state to allow the user to navigate back to previous frames.
 **/
export class FrameController extends Controller<HTMLElement> {

    static values = { id: String };

    // Can't use targets api here beecause the element might not be in the same DOM tree as the controller
    declare readonly idValue: string; 

    private form!: Form;
    private api!: FrameApi;
    private history!: FrameHistoryStrategy;

    initialize(): void {
        utils.addControllerAction(this, { method: 'requestUpdate:prevent'}); // Form submit will update the frame
    }

    injectContext({detail: {form, api, history}}: CustomEvent<FrameContext>) {
        this.form = form;
        this.api = api;
        this.history = history;
    }

    disconnect(): void {
        this.form = this.api = this.history = null;
    }

    /** Requests an initial load or reload of the content inside this frame */
    requestUpdate() {
        const variables = this.form.getVariables();
        this.api.getContent(this.resultElement, variables).then(content => {
            this.applyUpdate({content, variables});
        });
    }

    /** Should be called once the content has been loaded */
    applyUpdate({content, variables}: FrameUpdate) {
        this.show(content);
        this.history.captureState(variables);
    }

    private show(content: string) {
        this.resultElement.innerHTML = content;
    }

    get resultElement() {
        return document.getElementById(this.idValue)!;
    }
}